import type {
  ClothingSize,
  ClothingType,
  Collaborator,
  CollaboratorClothing,
  CollaboratorContract,
  CollaboratorEquipment,
  CollaboratorGender,
  CollaboratorRole,
  CollaboratorStatus,
  CollaboratorImportResult,
  CollaboratorUnavailability,
  CollaboratorWorkTime,
  ConstraintsReport,
  ContractType,
  InterimAgency,
  CopyPlanningWeekData,
  CopyPlanningWeekResponse,
  EquipmentType,
  OptimizePlanningSkippedShift,
  PlanningConfig,
  ProfessionalStatus,
  PublishPlanningSmsResponse,
  UnavailabityReason,
  WeekDay,
  WorkShift,
  WorkShiftClock,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'
import { extractContentDispositionFilename } from '../tools/contentDisposition.tools'

export type {
  CopyPlanningWeekData,
  CopyPlanningWeekResponse,
  CopyPlanningWeekSkippedShift,
  OptimizePlanningSkippedKind,
  OptimizePlanningSkippedShift,
} from '@gozoki/api-types'

// ============================================================================
// Collaborators mutations
// ============================================================================

export interface CreateCollaboratorData {
  // Required for CDD/CDI (manually entered), omitted for interim (auto-generated).
  matricule?: string
  firstName: string
  lastName: string
  email: string
  role: CollaboratorRole
  contractType: ContractType
  status: CollaboratorStatus
  // 'homme' | 'femme'.
  gender?: CollaboratorGender
  // ISO date (YYYY-MM-DD). null en update pour effacer une date existante.
  birthDate?: string | null
  // Champs CDI/CDD uniquement (ignorés/remis à null pour un intérimaire).
  professionalStatus?: ProfessionalStatus
  entryDate?: string
  seniorityDate?: string
  trialPeriodEndDate?: string
  phone: string
  // Requis sauf pour les rôles au sommet de hiérarchie (cf. rolesWithoutMandatoryManager).
  managerId?: number | null
  weeklyWorkingMinutes: number
  // Integer cents per hour.
  hourlyCost: number
  storeIds: number[]
  mainStoreId: number
  profilePicture?: File
  // Initial contract (historised). ISO date strings (YYYY-MM-DD).
  // Start date optional for interim (July fiche collab rules).
  startDate?: string
  // Required for interim.
  endDate?: string
  interimAgencyId?: number
}

export interface UpdateCollaboratorData extends Partial<
  Omit<CreateCollaboratorData, 'profilePicture' | 'endDate'>
> {
  // null transmet explicitement l'effacement de la date de fin (CDD -> CDI).
  endDate?: string | null
  // Flag explicite émis en multipart pour effacer endDate (le chemin JSON garde endDate:null).
  removeEndDate?: boolean
  profilePicture?: File
  removeProfilePicture?: boolean
  // Manual status override (set automatically when status is edited explicitly).
  statusOverride?: boolean
  // -15% offer access ("sans carte").
  offerAccessEnabled?: boolean
  offerAccessOverride?: boolean
}

const toCollaboratorFormData = (data: CreateCollaboratorData | UpdateCollaboratorData) => {
  const formData = new FormData()

  // En multipart, une valeur null est ignorée par la boucle ci-dessous. Pour effacer la date de
  // fin (CDD→CDI) on émet un flag explicite, façon removeProfilePicture.
  if ('endDate' in data && (data as UpdateCollaboratorData).endDate === null) {
    formData.append('removeEndDate', 'true')
  }

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue

    if (key === 'profilePicture' && value instanceof File) {
      formData.append(key, value)
      continue
    }

    if (key === 'storeIds' && Array.isArray(value)) {
      value.forEach((storeId) => formData.append('storeIds[]', String(storeId)))
      continue
    }

    formData.append(key, String(value))
  }

  return formData
}

// Create a collaborator
export const createCollaborator = async (data: CreateCollaboratorData) => {
  const hasProfilePicture = data.profilePicture instanceof File

  const response = hasProfilePicture
    ? await gozokiApiWithAuth.post<Collaborator>(
        '/hr/collaborators',
        toCollaboratorFormData(data),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
    : await gozokiApiWithAuth.post<Collaborator>('/hr/collaborators', data)

  return response.data
}

// Update a collaborator
export const updateCollaborator = async ({
  id,
  data,
}: {
  id: number
  data: UpdateCollaboratorData
}) => {
  const shouldUseMultipart =
    data.profilePicture instanceof File || data.removeProfilePicture !== undefined

  const response = shouldUseMultipart
    ? await gozokiApiWithAuth.patch<Collaborator>(
        `/hr/collaborators/${id}`,
        toCollaboratorFormData(data),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
    : await gozokiApiWithAuth.patch<Collaborator>(`/hr/collaborators/${id}`, data)

  return response.data
}

// Delete a collaborator
export const deleteCollaborator = async (id: number) => {
  const response = await gozokiApiWithAuth.delete(`/hr/collaborators/${id}`)
  return response
}

// Mass import (MAJ par matricule) depuis un fichier .xlsx
export const importCollaborators = async (file: File): Promise<CollaboratorImportResult> => {
  const formData = new FormData()
  formData.append('importFile', file)
  const response = await gozokiApiWithAuth.post<CollaboratorImportResult>(
    '/hr/collaborators/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

// ============================================================================
// Collaborator contracts (historised per contract/mission)
// ============================================================================

export interface CollaboratorContractData {
  contractType: ContractType
  // Required for interim.
  interimAgencyId?: number
  weeklyWorkingMinutes: number
  // ISO date strings (YYYY-MM-DD). endDate required for interim, startDate optional
  // for interim (July fiche collab rules).
  startDate?: string
  endDate?: string
}

export const createCollaboratorContract = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CollaboratorContractData
}) => {
  const response = await gozokiApiWithAuth.post<CollaboratorContract>(
    `/hr/collaborators/${collaboratorId}/contracts`,
    data,
  )
  return response.data
}

export const updateCollaboratorContract = async ({
  collaboratorId,
  contractId,
  data,
}: {
  collaboratorId: number
  contractId: number
  data: Partial<Omit<CollaboratorContractData, 'contractType'>>
}) => {
  const response = await gozokiApiWithAuth.patch<CollaboratorContract>(
    `/hr/collaborators/${collaboratorId}/contracts/${contractId}`,
    data,
  )
  return response.data
}

// ============================================================================
// Interim agencies (referential)
// ============================================================================

export interface InterimAgencyData {
  name: string
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
}

export const createInterimAgency = async (data: InterimAgencyData) => {
  const response = await gozokiApiWithAuth.post<InterimAgency>('/hr/interim-agencies', data)
  return response.data
}

export const updateInterimAgency = async ({
  id,
  data,
}: {
  id: number
  data: Partial<InterimAgencyData> & { isArchived?: boolean }
}) => {
  const response = await gozokiApiWithAuth.patch<InterimAgency>(`/hr/interim-agencies/${id}`, data)
  return response.data
}

export const archiveInterimAgency = async (id: number) => {
  const response = await gozokiApiWithAuth.delete<InterimAgency>(`/hr/interim-agencies/${id}`)
  return response.data
}

// ============================================================================
// Collaborator formation lifecycle (manager actions)
// ============================================================================

export interface StartCollaboratorFormationData {
  startDate?: string // Format: "YYYY-MM-DD"
  endDate?: string // Format: "YYYY-MM-DD"
}

// Bornes facultatives : sans `startDate`, la formation démarre aujourd'hui.
export const startCollaboratorFormation = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data?: StartCollaboratorFormationData
}) => {
  const response = await gozokiApiWithAuth.post<Collaborator>(
    `/hr/collaborators/${collaboratorId}/formation/start`,
    data,
  )
  return response.data
}

export interface UpdateCollaboratorFormationData {
  startDate: string // Format: "YYYY-MM-DD"
  endDate: string | null // null = aucun terme déclaré
}

// Les deux dates sont toujours transmises : le backend compare aux valeurs stockées pour refuser
// de déplacer une date relevant d'un planning passé.
export const updateCollaboratorFormation = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: UpdateCollaboratorFormationData
}) => {
  const response = await gozokiApiWithAuth.patch<Collaborator>(
    `/hr/collaborators/${collaboratorId}/formation`,
    data,
  )
  return response.data
}

// ============================================================================
// Collaborator work times mutations (batch operations)
// ============================================================================

export interface CreateCollaboratorWorkTimeData {
  dayOfWeek: WeekDay
  startTime: string // Format: "HH:mm"
  endTime: string // Format: "HH:mm"
}

// Replace-all work times for a collaborator.
// We use PUT to make the replace semantics explicit and avoid partial updates.
export const replaceCollaboratorWorkTimes = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorWorkTimeData[]
}) => {
  const response = await gozokiApiWithAuth.put<CollaboratorWorkTime[]>(
    `/hr/collaborators/${collaboratorId}/work-times`,
    { workTimes: data },
  )
  return response.data
}

// ============================================================================
// Collaborator unavailabilities mutations
// ============================================================================

export interface CreateCollaboratorUnavailabilityData {
  startDate: string // ISO date string
  endDate: string // ISO date string
  startTime?: string | null // Format: "HH:mm"
  endTime?: string | null // Format: "HH:mm"
  reason: UnavailabityReason
}

export type UpdateCollaboratorUnavailabilityData = Partial<CreateCollaboratorUnavailabilityData>

// Create an unavailability for a collaborator
export const createCollaboratorUnavailability = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorUnavailabilityData
}) => {
  const response = await gozokiApiWithAuth.post<CollaboratorUnavailability>(
    `/hr/collaborators/${collaboratorId}/unavailabilities`,
    data,
  )
  return response.data
}

// Update an unavailability for a collaborator
export const updateCollaboratorUnavailability = async ({
  collaboratorId,
  unavailabilityId,
  data,
}: {
  collaboratorId: number
  unavailabilityId: number
  data: UpdateCollaboratorUnavailabilityData
}) => {
  const response = await gozokiApiWithAuth.patch<CollaboratorUnavailability>(
    `/hr/collaborators/${collaboratorId}/unavailabilities/${unavailabilityId}`,
    data,
  )
  return response.data
}

// Delete an unavailability for a collaborator
export const deleteCollaboratorUnavailability = async ({
  collaboratorId,
  unavailabilityId,
}: {
  collaboratorId: number
  unavailabilityId: number
}) => {
  const response = await gozokiApiWithAuth.delete(
    `/hr/collaborators/${collaboratorId}/unavailabilities/${unavailabilityId}`,
  )
  return response
}

export interface ExportCollaboratorUnavailabilitiesToXlsxParams {
  storeId?: number
}

export const exportCollaboratorUnavailabilitiesToXlsx = async ({
  storeId,
}: ExportCollaboratorUnavailabilitiesToXlsxParams = {}) => {
  const response = await gozokiApiWithAuth.get<Blob>(
    '/hr/collaborators/unavailabilities/export-xlsx',
    {
      responseType: 'blob',
      params: { storeId },
    },
  )
  return response.data
}

export interface ExportCollaboratorsToXlsxParams {
  storeId?: number
}

export interface ExportCollaboratorsToXlsxResult {
  blob: Blob
  filename: string
}

export const exportCollaboratorsToXlsx = async ({
  storeId,
}: ExportCollaboratorsToXlsxParams = {}): Promise<ExportCollaboratorsToXlsxResult> => {
  const response = await gozokiApiWithAuth.get<Blob>('/hr/collaborators/export-xlsx', {
    responseType: 'blob',
    params: { storeId },
  })
  return {
    blob: response.data,
    filename: extractContentDispositionFilename(
      response.headers['content-disposition'],
      'fiches-collaborateurs.xlsx',
    ),
  }
}

// ============================================================================
// Collaborator equipment mutations
// ============================================================================

export interface CreateCollaboratorEquipmentData {
  type: EquipmentType
  givenDate: string // ISO date string
  serialNumber: string
  brand: string
}

export type UpdateCollaboratorEquipmentData = Partial<CreateCollaboratorEquipmentData>

// Create equipment for a collaborator
export const createCollaboratorEquipment = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorEquipmentData
}) => {
  const response = await gozokiApiWithAuth.post<CollaboratorEquipment>(
    `/hr/collaborators/${collaboratorId}/equipment`,
    data,
  )
  return response.data
}

// Update equipment for a collaborator
export const updateCollaboratorEquipment = async ({
  collaboratorId,
  equipmentId,
  data,
}: {
  collaboratorId: number
  equipmentId: number
  data: UpdateCollaboratorEquipmentData
}) => {
  const response = await gozokiApiWithAuth.patch<CollaboratorEquipment>(
    `/hr/collaborators/${collaboratorId}/equipment/${equipmentId}`,
    data,
  )
  return response.data
}

// Delete equipment for a collaborator
export const deleteCollaboratorEquipment = async ({
  collaboratorId,
  equipmentId,
}: {
  collaboratorId: number
  equipmentId: number
}) => {
  const response = await gozokiApiWithAuth.delete(
    `/hr/collaborators/${collaboratorId}/equipment/${equipmentId}`,
  )
  return response
}

// ============================================================================
// Collaborator clothing mutations
// ============================================================================

export interface CreateCollaboratorClothingData {
  type: ClothingType
  size: ClothingSize
  quantity: number
}

export type UpdateCollaboratorClothingData = Partial<CreateCollaboratorClothingData>

// Create clothing for a collaborator
export const createCollaboratorClothing = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: CreateCollaboratorClothingData
}) => {
  const response = await gozokiApiWithAuth.post<CollaboratorClothing>(
    `/hr/collaborators/${collaboratorId}/clothing`,
    data,
  )
  return response.data
}

// Update clothing for a collaborator
export const updateCollaboratorClothing = async ({
  collaboratorId,
  clothingId,
  data,
}: {
  collaboratorId: number
  clothingId: number
  data: UpdateCollaboratorClothingData
}) => {
  const response = await gozokiApiWithAuth.patch<CollaboratorClothing>(
    `/hr/collaborators/${collaboratorId}/clothing/${clothingId}`,
    data,
  )
  return response.data
}

// Delete clothing for a collaborator
export const deleteCollaboratorClothing = async ({
  collaboratorId,
  clothingId,
}: {
  collaboratorId: number
  clothingId: number
}) => {
  const response = await gozokiApiWithAuth.delete(
    `/hr/collaborators/${collaboratorId}/clothing/${clothingId}`,
  )
  return response
}

// ============================================================================
// Collaborator goods (equipment + clothing) sync
// ============================================================================

export interface SyncCollaboratorGoodsData {
  objects: (CreateCollaboratorEquipmentData & { id?: number })[]
  clothes: (CreateCollaboratorClothingData & { id?: number })[]
}

// Sync matériel + vêtements en un seul appel : le diff (create/update/delete) est fait côté serveur
// dans une transaction. Renvoie le collaborateur à jour (equipment + clothing préchargés).
export const syncCollaboratorGoods = async ({
  collaboratorId,
  data,
}: {
  collaboratorId: number
  data: SyncCollaboratorGoodsData
}) => {
  const response = await gozokiApiWithAuth.put<Collaborator>(
    `/hr/collaborators/${collaboratorId}/goods`,
    data,
  )
  return response.data
}

// ============================================================================
// Work shifts mutations
// ============================================================================

export interface CreateWorkShiftData {
  collaboratorId: number
  storeId: number
  date: string // ISO date string
  startTime: string // Format: "HH:mm"
  endTime: string // Format: "HH:mm"
}

export type UpdateWorkShiftData = Partial<CreateWorkShiftData>

export interface CrossStoreSegment {
  storeId: number
  startTime: string // "HH:mm"
  endTime: string // "HH:mm"
}

export interface CreateCrossStoreWorkShiftsData {
  collaboratorId: number
  date: string // ISO date string
  stores: CrossStoreSegment[] // chaîne ordonnée de 2 à 4 boutiques
}

export interface UpdateWorkShiftValidatedTimesData {
  validatedStartTime?: string | null
  validatedEndTime?: string | null
}

// Create a work shift
export const createWorkShift = async (data: CreateWorkShiftData) => {
  const response = await gozokiApiWithAuth.post<WorkShift>('/hr/work-shifts', data)
  return response.data
}

// Create all shifts of a multi-store move (transport derived afterwards).
// `transportGenerated` indicates whether at least one paid transport segment is actually derived
// (gap < 45 min between two consecutive stores) so the UI can show an accurate toast.
export interface CreateCrossStoreWorkShiftsResponse {
  shifts: WorkShift[]
  transportGenerated: boolean
}

export const createCrossStoreWorkShifts = async (data: CreateCrossStoreWorkShiftsData) => {
  const response = await gozokiApiWithAuth.post<CreateCrossStoreWorkShiftsResponse>(
    '/hr/work-shifts/cross-store',
    data,
  )
  return response.data
}

// Update a work shift
export const updateWorkShift = async ({ id, data }: { id: number; data: UpdateWorkShiftData }) => {
  const response = await gozokiApiWithAuth.patch<WorkShift>(`/hr/work-shifts/${id}`, data)
  return response.data
}

export const updateWorkShiftValidatedTimes = async ({
  id,
  data,
}: {
  id: number
  data: UpdateWorkShiftValidatedTimesData
}) => {
  const response = await gozokiApiWithAuth.patch<WorkShift>(
    `/hr/work-shifts/${id}/validated-times`,
    data,
  )
  return response.data
}

export const unvalidateWorkShift = async (id: number) => {
  const response = await gozokiApiWithAuth.post<WorkShift>(`/hr/work-shifts/${id}/unvalidate`)
  return response.data
}

export const validateWorkShift = async (id: number) => {
  const response = await gozokiApiWithAuth.post<WorkShift>(`/hr/work-shifts/${id}/validate`)
  return response.data
}

// Delete a work shift
export const deleteWorkShift = async (id: number) => {
  const response = await gozokiApiWithAuth.delete(`/hr/work-shifts/${id}`)
  return response
}

// ============================================================================
// Attendance mutations
// ============================================================================

export interface ClockInData {
  collaboratorId: number
  storeId: number
  temoinFile: File | Blob
  workShiftId?: number | null
  childShiftId?: number | null
  comment?: string
}

export interface ClockOutData {
  collaboratorId: number
  storeId: number
  temoinFile: File | Blob
  workShiftId: number | null
  childShiftId?: number | null
  comment?: string
}

export interface ExportWorkShiftClocksToXlsxParams {
  // Boutiques sélectionnées sur la page (multi-boutique). Vide/absent ⇒ toutes les boutiques.
  storeIds?: number[]
  dateStart: string
  dateEnd: string
  // Inclure les créneaux non encore validés (pointage brut collaborateur) en plus des
  // pointages validés. Par défaut, seuls les pointages validés sont exportés.
  includeUnvalidated?: boolean
}

export interface ExportPlanningWorkShiftsToXlsxParams {
  // Boutiques sélectionnées sur la page (multi-boutique). Vide/absent ⇒ toutes les boutiques.
  storeIds?: number[]
  dateStart: string
  dateEnd: string
}

// Clock in for a collaborator
export const clockIn = async (data: ClockInData) => {
  const formData = new FormData()
  formData.append('collaboratorId', data.collaboratorId.toString())
  formData.append('temoinFile', data.temoinFile)
  formData.append('storeId', data.storeId.toString())
  if (data.workShiftId === null) {
    formData.append('workShiftId', '')
  } else if (data.workShiftId !== undefined) {
    formData.append('workShiftId', data.workShiftId.toString())
  }
  if (data.childShiftId != null) {
    formData.append('childShiftId', data.childShiftId.toString())
  }
  if (data.comment !== undefined && data.comment !== '') {
    formData.append('comment', data.comment)
  }

  const response = await gozokiApiWithAuth.post<WorkShiftClock>(
    '/hr/attendances/clock-in',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

// Clock out for a collaborator
export const clockOut = async (data: ClockOutData) => {
  const formData = new FormData()
  formData.append('collaboratorId', data.collaboratorId.toString())
  formData.append('temoinFile', data.temoinFile)
  formData.append('storeId', data.storeId.toString())
  if (data.workShiftId !== null) {
    formData.append('workShiftId', data.workShiftId.toString())
  }
  if (data.childShiftId != null) {
    formData.append('childShiftId', data.childShiftId.toString())
  }
  if (data.comment !== undefined && data.comment !== '') {
    formData.append('comment', data.comment)
  }

  const response = await gozokiApiWithAuth.post<WorkShiftClock>(
    '/hr/attendances/clock-out',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

export const exportWorkShiftClocksToXlsx = async (data: ExportWorkShiftClocksToXlsxParams) => {
  const { storeIds, ...rest } = data
  // GET : liste boutiques en CSV (`storeIds=1,2`), convention du repo pour les params liste.
  const params: Record<string, string | number | boolean> = { ...rest }
  if (storeIds && storeIds.length > 0) {
    params.storeIds = storeIds.join(',')
    if (storeIds.length === 1) params.storeId = storeIds[0]
  }

  const response = await gozokiApiWithAuth.get<Blob>('/hr/attendances/export-xlsx', {
    params,
    responseType: 'blob',
  })
  return response.data
}

export const exportPlanningWorkShiftsToXlsx = async (
  data: ExportPlanningWorkShiftsToXlsxParams,
) => {
  const response = await gozokiApiWithAuth.post<Blob>('/hr/planning/export-xlsx', data, {
    responseType: 'blob',
  })
  return response.data
}

// ============================================================================
// Planning mutations
// ============================================================================

export interface OptimizePlanningData {
  dateStart: string // ISO date string (YYYY-MM-DD)
  dateEnd: string // ISO date string (YYYY-MM-DD)
}

export interface PublishPlanningSmsData {
  storeId: number
  dateStart: string // ISO date string (YYYY-MM-DD)
  dateEnd: string // ISO date string (YYYY-MM-DD)
  // Optionnel : restreint l'envoi à ces collaborateurs. Absent/vide ⇒ tous ceux ayant un shift.
  recipientCollaboratorIds?: number[]
}

export interface OptimizePlanningResponse {
  status: 'ok' | 'partial'
  createdShifts: number
  updatedShifts: number
  skipped: number
  skippedShifts: OptimizePlanningSkippedShift[]
  constraintsReport: ConstraintsReport
}

// Update planning configuration (global + per-store)
export interface UpdatePlanningConfigData {
  global: {
    maxWeeklyHours: number
    maxDailyHours: number
    maxShiftDurationHours: number
    maxConsecutiveWorkDays: number
    minWeeklyRestHours: number
    breakTriggerHours: number
    breakDurationMinutes: number
    maxDailyBreaks: number
    nightStartMinutes: number
    nightEndMinutes: number
    nightMultiplierCenti: number
    weekendMultiplierCenti: number
    nightWeekendMultiplierCenti: number
    etpTargetMin: number
    etpTargetMax: number
    fpCaTargetMin: number
    fpCaTargetMax: number
    okAttenteDeadlineWeekday: number
    okAttenteDeadlineMinutes: number
  }
  storeConfigs: {
    storeId: number
    lunchBreakMinutes: number
    staffingRules: {
      minTurnover: number
      maxTurnover: number
      staffCount: number
    }[]
  }[]
}

export const updatePlanningConfig = async (data: UpdatePlanningConfigData) => {
  const response = await gozokiApiWithAuth.put<PlanningConfig>('/hr/planning/config', data)
  return response.data
}

// Optimize planning
export const optimizePlanning = async (data: OptimizePlanningData) => {
  const response = await gozokiApiWithAuth.post<OptimizePlanningResponse>(
    '/hr/planning/optimize',
    data,
  )
  return response.data
}

export const copyPlanningWeek = async (data: CopyPlanningWeekData) => {
  const response = await gozokiApiWithAuth.post<CopyPlanningWeekResponse>(
    '/hr/planning/copy-week',
    data,
  )
  return response.data
}

export const publishPlanningSms = async (data: PublishPlanningSmsData) => {
  const response = await gozokiApiWithAuth.post<PublishPlanningSmsResponse>(
    '/hr/planning/publish',
    data,
  )
  return response.data
}
